class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);

    /* Mojo Change - keyboard use of the cart drawer, in mojoCartDrawerKeyboard at the end of this file (MCHN-14, scan findings 1 and 2, 2026-10-16) */
    if (this.drawer) mojoCartDrawerKeyboard(this.drawer);
    /* Mojo Change */
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // While the drawer is open (upsell adds, gift wrap, quick view),
    // patch the money-touched regions instead of swapping the whole
    // drawer — keeps open collapsibles, scroll and upsell state intact.
    const drawerOpen = this.drawer && !!this.drawer.querySelector('details[open]');

    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) return;

      if (
        section.id === 'mini-cart' &&
        drawerOpen &&
        window.theme && theme.patchMiniCart &&
        parsedState.sections[section.id] &&
        theme.patchMiniCart(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector))
      ) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);

/* Mojo Change - keyboard focus in the cart drawer (MCHN-14, scan findings 1 and 2, 2026-10-16).
   Finding 1: when the drawer opens other than from the cart icon, after Add to cart, focus moves
   into it; Tab and Shift+Tab stay inside it while it is open; when it closes, focus goes back to
   what opened it. Finding 2: the drawer's close button takes a Tab stop, is announced as a button,
   and works with Enter and Space. */
function mojoCartDrawerKeyboard(drawer) {
  if (drawer.mojoKeyboardReady) return;
  if (typeof drawer.openMenuDrawer !== 'function') {
    customElements.whenDefined('cart-drawer').then(() => mojoCartDrawerKeyboard(drawer));
    return;
  }
  drawer.mojoKeyboardReady = true;

  const focusableItems = () => {
    const panel = drawer.querySelector('details > .cart-drawer');
    if (!panel) return [];
    return getFocusableElements(panel).filter((element) =>
      element.tabIndex >= 0 &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden'
    );
  };

  const tagCloseButtons = () => {
    drawer.querySelectorAll('drawer-close-button').forEach((button) => {
      if (!button.hasAttribute('role')) button.setAttribute('role', 'button');
      if (!button.hasAttribute('tabindex')) button.setAttribute('tabindex', '0');
    });
  };
  tagCloseButtons();

  // When the theme re-renders the drawer's contents, the element that had focus is removed and
  // focus falls to the page; it is put back inside the drawer.
  let focusInside = false;
  drawer.addEventListener('focusin', () => { focusInside = true; });
  drawer.addEventListener('focusout', (event) => {
    if (event.relatedTarget && !drawer.contains(event.relatedTarget)) focusInside = false;
  });
  new MutationObserver(() => {
    tagCloseButtons();
    const details = drawer.querySelector('details');
    if (!focusInside || !details || !details.hasAttribute('open')) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    const items = focusableItems();
    if (items.length) items[0].focus();
  }).observe(drawer, { childList: true, subtree: true });

  const openMenuDrawer = drawer.openMenuDrawer.bind(drawer);
  drawer.openMenuDrawer = (summaryElement = false) => {
    drawer.mojoOpener = summaryElement || document.activeElement;
    openMenuDrawer(summaryElement);
    if (!summaryElement) {
      // The drawer becomes visible, and so focusable, only once its opening transition starts.
      let tries = 0;
      const moveIn = () => {
        const details = drawer.querySelector('details');
        if (!details || !details.hasAttribute('open')) return;
        const items = focusableItems();
        if (items.length) items[0].focus();
        if (!items.includes(document.activeElement) && ++tries < 30) setTimeout(moveIn, 50);
      };
      setTimeout(moveIn, 50);
    }
  };

  drawer.addEventListener('close', () => {
    const opener = drawer.mojoOpener;
    drawer.mojoOpener = null;
    const target = opener && document.body.contains(opener) ? opener : drawer.querySelector('details > summary');
    if (target) target.focus();
  });

  drawer.addEventListener('keydown', (event) => {
    const closeButton = event.target.closest ? event.target.closest('drawer-close-button') : null;
    if (closeButton && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      closeButton.click();
      return;
    }

    const details = drawer.querySelector('details');
    if (event.key !== 'Tab' || !details || !details.hasAttribute('open')) return;
    const items = focusableItems();
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    const inside = items.includes(document.activeElement);

    if (event.shiftKey && (document.activeElement === first || !inside)) {
      event.preventDefault();
      event.stopPropagation();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
      event.preventDefault();
      event.stopPropagation();
      first.focus();
    }
  });
}
/* Mojo Change */
